import { useEffect, useMemo, useState } from 'react';
import { Polygon as LeafletPolygon, Popup } from 'react-leaflet';
import { loadTenantBoundaries, type BoundaryRecord } from '@/utils/boundaryCache';
import {
  geometryToPolygons,
  boundaryStyle,
  indexedBoundaryStyle,
} from '@/utils/boundaryGeometry';

interface BoundariesOverlayProps {
  /** Tenant whose boundaries to fetch. Renders nothing when null. */
  tenantId: number | null;
  /** Master toggle from MapSettings. */
  enabled: boolean;
  /**
   * Set of boundary IDs the user has enabled. Pass `null` to render every
   * boundary (the implicit "show all" default for fresh users).
   */
  enabledIds?: Set<number> | null;
  /** Called once the boundaries list is fetched, so the parent can populate
   *  the settings UI with the available list. */
  onBoundariesLoaded?: (boundaries: BoundaryRecord[]) => void;
  /**
   * Colour scheme. `'semantic'` (default): primary = orange, others = purple.
   * `'indexed'`: each boundary gets a distinct palette colour by its index —
   * used by the admin parcels map so co-located boundaries are tellable apart.
   */
  colorMode?: 'semantic' | 'indexed';
  /**
   * When `false`, boundary polygons are rendered non-interactive
   * (`pointer-events: none`) and their popups are suppressed, so clicks fall
   * through to the parcel layer beneath. MapCanvas sets this to `false`
   * whenever parcels are shown and tappable — otherwise the boundary path
   * (in `overlayPane`, above the parcel canvas) swallows the click and a
   * boundary popup always wins over the parcel popup. Defaults to `true`.
   */
  interactive?: boolean;
}

// Re-exported for the existing importers (MapCanvas, MapSettings); the type
// now lives with the cache that produces it.
export type { BoundaryRecord };

interface RenderablePolygon {
  key: string;
  positions: [number, number][];
  name: string;
  is_primary: boolean;
  description?: string | null;
  colorIndex: number; // stable index in the full boundary list (indexed mode)
}

/**
 * Renders an FPA's boundary polygons as outlined Leaflet shapes. Primary
 * boundary is highlighted in primary brand colour; secondary boundaries use
 * a neutral outline so they don't compete visually.
 *
 * Auth note: the backend endpoint requires the BOUNDARY/LIST permission, so
 * MapSettings should disable the boundaries toggle for non-staff users.
 */
export function BoundariesOverlay({
  tenantId,
  enabled,
  enabledIds,
  onBoundariesLoaded,
  colorMode = 'semantic',
  interactive = true,
}: BoundariesOverlayProps) {
  const [boundaries, setBoundaries] = useState<BoundaryRecord[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || tenantId == null) return;
    let cancelled = false;
    // Shared with useFpaMapHome — whichever asks first pays for the fetch.
    loadTenantBoundaries(tenantId).then((list) => {
      if (cancelled) return;
      setBoundaries(list);
      setLoaded(true);
      onBoundariesLoaded?.(list);
    });
    return () => {
      cancelled = true;
    };
    // onBoundariesLoaded intentionally excluded — parent may recreate the
    // callback identity on every render; we only care about tenant/enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, enabled]);

  const polygons = useMemo<RenderablePolygon[]>(() => {
    if (!enabled || !loaded) return [];
    // Stable colour index = position in the full list, so toggling boundaries
    // doesn't reshuffle indexed colours.
    return boundaries.flatMap((b, idx) => {
      if (enabledIds && !enabledIds.has(b.id)) return [];
      return geometryToPolygons(b.geometry, b.id).map((poly) => ({
        ...poly,
        name: b.name,
        is_primary: Boolean(b.is_primary),
        description: b.description ?? null,
        colorIndex: idx,
      }));
    });
  }, [enabled, loaded, boundaries, enabledIds]);

  if (!enabled || polygons.length === 0) return null;

  return (
    <>
      {polygons.map((poly) => (
        <LeafletPolygon
          key={poly.key}
          positions={poly.positions}
          pathOptions={{
            ...(colorMode === 'indexed'
              ? indexedBoundaryStyle(poly.colorIndex)
              : boundaryStyle(poly.is_primary)),
            interactive,
          }}
        >
          {interactive && (
            <Popup>
              <div className="text-xs">
                <div className="font-semibold">{poly.name}</div>
                {poly.is_primary && (
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Primary
                  </div>
                )}
                {poly.description && <div>{poly.description}</div>}
              </div>
            </Popup>
          )}
        </LeafletPolygon>
      ))}
    </>
  );
}

export default BoundariesOverlay;
